'use client';

import { usePathname } from 'next/navigation';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Classes from './style.module.scss';

import breakpoints from '@/lib/breakpoints';
import { EnvironmentService } from '@/lib/environmentVariables';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useScrollSpy from '@/hooks/useScrollSpy';
import useSocialMedia from '@/hooks/useSocialMedia';

import { navigationStore } from '@/store/navigation';

import { siteConfig } from '@/constant/config';
import { Share, SocialMediaList } from '@/models/SocialMedia';
import { NavigationActive } from '@/models/States';

import IconButton from '../buttons/IconButton';
import Glow from '../Glow';
import Hamburger from '../Hamburger';
import {
  CloseIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  ShareIcon,
  TwitterIcon,
  YouTubeIcon,
} from '../icons';
import IconLink from '../links/IconLink';
import UnstyledLink from '../links/UnstyledLink';

type NavigationItem = {
  sectiontitle: string;
  sectionid: string;
};

type NavigationHomePageProps = {
  navigationlist: NavigationItem[];
  socialmedia?: SocialMediaList;
  share?: Share;
};

export default function NavigationHomePageMobile(
  props: NavigationHomePageProps,
) {
  const { navigationlist, socialmedia, share } = props;
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.lg})`);
  const [desktop, setDesktop]: [
    boolean | undefined,
    Dispatch<SetStateAction<boolean | undefined>>,
  ] = useState();
  const pathname = usePathname();
  const pageURL = EnvironmentService.getCanonicalURL().replace(':', '%3A');
  const [shareButtons, setShareButtons] = useState<boolean>(false);
  const shareButtonsRef = useRef<HTMLButtonElement>(null);
  const filterByShowInSidebar = useSocialMedia(socialmedia);
  const anyShare = useMemo(
    () => share && Object.values(share).some(Boolean),
    [share],
  );
  const showShareButtons = useCallback(() => {
    const target = shareButtonsRef.current;

    if (!target) return;

    setShareButtons(!shareButtons);
  }, [shareButtons]);
  const activeId = useScrollSpy(
    navigationlist.map((item) => item.sectionid),
    150,
    'key-visual',
  );
  const toogelNav = navigationStore(
    (store: NavigationActive) => store.toggleNavigation,
  );
  const activeNav = navigationStore(
    (store: NavigationActive) => store.navigation,
  );
  const [itemId, setItemId] = useState<string | null>(null);

  useEffect(() => {
    if (itemId && itemId === activeId) {
      toogelNav(false);
      setItemId(null);
    }
  }, [itemId, activeId, toogelNav]);

  useEffect(() => {
    if (!activeNav.opened) {
      setItemId(null);
    }
  }, [activeNav]);

  useEffect(() => {
    if (!activeNav.opened && shareButtons) setShareButtons(false);
  }, [activeNav, shareButtons]);

  useEffect(() => {
    setDesktop(isDesktop);
  }, [isDesktop]);

  if (desktop !== false) return <></>;

  return (
    <>
      <nav
        className={cn(
          'navigation-mobile',
          'fixed left-0 top-0 flex h-screen w-screen transform flex-col items-center justify-start gap-6 overflow-hidden bg-primary-950 py-6',
          activeNav.opened
            ? 'pointer-events-auto z-[999] -translate-y-0 opacity-100'
            : '-translate-y-100 pointer-events-none -z-10 opacity-0',
        )}
      >
        <div className='z-[999] flex w-screen items-center justify-between gap-4 px-6'>
          {activeNav.opened && (
            <div className='social-media'>
              <ul
                className={cn(
                  'social-media__mobile flex flex-row gap-4 divide-gray-300 transition-opacity ease-linear md:gap-6',
                  activeNav.opened ? 'opacity-100' : 'opacity-0',
                )}
              >
                {filterByShowInSidebar && (
                  <>
                    {filterByShowInSidebar.facebook && (
                      <li className='mb-0 block'>
                        <IconLink
                          href={filterByShowInSidebar.facebook.url}
                          icon={FacebookIcon}
                          iconPostion='start'
                          variant='unstyled'
                          className='relative min-h-[28px] min-w-[28px] gap-2 rounded-full p-1 transition-colors duration-150 ease-linear md:min-h-[46px] md:min-w-[46px] md:p-2'
                          classNames={{
                            icon: 'md:w-6 md:h-6 w-5 h-5 ml-[2px] fill-white',
                          }}
                        >
                          <span className='pointer-events-none inline-table pr-1 text-white transition-all duration-300 ease-out'>
                            Facebook
                          </span>
                        </IconLink>
                      </li>
                    )}
                    {filterByShowInSidebar.instagram && (
                      <li className='mb-0 block'>
                        <IconLink
                          href={filterByShowInSidebar.instagram.url}
                          icon={InstagramIcon}
                          iconPostion='start'
                          variant='unstyled'
                          className='relative min-h-[28px] min-w-[28px] gap-2 rounded-full p-1 transition-colors duration-150 ease-linear md:min-h-[46px] md:min-w-[46px] md:p-2'
                          classNames={{
                            icon: 'md:w-6 md:h-6 w-5 h-5 ml-[2px] fill-white',
                          }}
                        >
                          <span className='pointer-events-none inline-table pr-1 text-white transition-all duration-300 ease-out'>
                            Instagram
                          </span>
                        </IconLink>
                      </li>
                    )}
                    {filterByShowInSidebar.linkedin && (
                      <li className='mb-0 block'>
                        <IconLink
                          href={filterByShowInSidebar.linkedin.url}
                          icon={LinkedinIcon}
                          iconPostion='start'
                          variant='unstyled'
                          className='relative min-h-[28px] min-w-[28px] gap-2 rounded-full p-1 transition-colors duration-150 ease-linear md:min-h-[46px] md:min-w-[46px] md:p-2'
                          classNames={{
                            icon: 'md:w-6 md:h-6 w-5 h-5 ml-[2px] fill-white',
                          }}
                        >
                          <span className='pointer-events-none inline-table pr-1 text-white transition-all duration-300 ease-out'>
                            Linkedin
                          </span>
                        </IconLink>
                      </li>
                    )}
                    {filterByShowInSidebar.youtube && (
                      <li className='mb-0 block'>
                        <IconLink
                          href={filterByShowInSidebar.youtube.url}
                          icon={YouTubeIcon}
                          iconPostion='start'
                          variant='unstyled'
                          className='relative min-h-[28px] min-w-[28px] gap-2 rounded-full p-1 transition-colors duration-150 ease-linear md:min-h-[46px] md:min-w-[46px] md:p-2'
                          classNames={{
                            icon: 'md:w-6 md:h-6 w-5 h-5 ml-[2px] fill-white',
                          }}
                        >
                          <span className='pointer-events-none inline-table pr-1 text-white transition-all duration-300 ease-out'>
                            Youtube
                          </span>
                        </IconLink>
                      </li>
                    )}
                    {filterByShowInSidebar.twitter && (
                      <li className='mb-0 block'>
                        <IconLink
                          href={filterByShowInSidebar.twitter.url}
                          icon={TwitterIcon}
                          iconPostion='start'
                          variant='unstyled'
                          className='relative min-h-[28px] min-w-[28px] gap-2 rounded-full p-1 transition-colors duration-150 ease-linear md:min-h-[46px] md:min-w-[46px] md:p-2'
                          classNames={{
                            icon: 'md:w-6 md:h-6 w-5 h-5 ml-[2px] fill-white',
                          }}
                        >
                          <span className='pointer-events-none inline-table pr-1 text-white transition-all duration-300 ease-out'>
                            Twitter
                          </span>
                        </IconLink>
                      </li>
                    )}
                  </>
                )}
                {anyShare && (
                  <li className='social-media__share block'>
                    <IconButton
                      icon={ShareIcon}
                      variant='light'
                      className={cn(
                        'min-h-[28px] min-w-[28px] gap-2 rounded-full border-none p-1 transition-colors duration-150 ease-linear first-line:relative hover:bg-transparent active:bg-transparent md:min-h-[46px] md:min-w-[46px] md:p-2',
                      )}
                      classNames={{
                        icon: `md:w-6 md:h-6 w-4 h-4 ml-[2px] fill-white`,
                      }}
                      onClick={showShareButtons}
                      ref={shareButtonsRef}
                    >
                      <span className='pointer-events-none inline-table pr-1 text-white transition-all duration-300 ease-out'>
                        Share
                      </span>
                    </IconButton>
                  </li>
                )}
              </ul>
            </div>
          )}
          <Hamburger location='navigation' />
        </div>
        <ul className='relative z-10 my-auto grid w-fit items-center gap-6 overflow-y-auto overflow-x-hidden p-0 text-2xl sm:grid-cols-2 sm:gap-x-10 sm:gap-y-4'>
          {navigationlist.map((item, index: number) => (
            <li
              key={item.sectionid}
              className={cn(
                'relative block w-full sm:mb-2',
                'transition-opacity duration-500 ease-linear',
                'before:pointer-events-none before:absolute before:top-1/2 before:block before:h-px before:w-4 before:-translate-y-1/2 before:transform before:bg-blue-600 before:transition-opacity before:duration-300 before:ease-linear',
                item.sectionid === activeId
                  ? 'before:opacity-100 before:delay-150'
                  : 'before:opacity-0',
                activeNav.opened ? 'opacity-100' : 'opacity-0',
              )}
              style={{
                transitionDelay: activeNav.opened
                  ? `${250 + 75 * index}ms`
                  : '0ms',
              }}
              onClick={() => setItemId(item.sectionid)}
            >
              <UnstyledLink
                href={`#${item.sectionid}`}
                className={cn(
                  'block w-full leading-normal',
                  'transition-all duration-300 ease-linear',
                  item.sectionid === activeId
                    ? 'translate-x-8 transform text-white'
                    : 'text-gray-300',
                )}
                scrollToAnchor
              >
                {item.sectiontitle}
              </UnstyledLink>
            </li>
          ))}
        </ul>
        <Glow
          className={cn(
            '-bottom-[300px] -left-[550px] z-0 h-[692px] w-[692px] md:-bottom-[300px] md:-left-[550px] md:h-[692px] md:w-[692px]',
            'transition-opacity duration-500 ease-linear',
            activeNav.opened ? 'opacity-100 delay-500' : 'opacity-0',
          )}
          style={{ transform: 'translate3d(0, 0, 0)' }}
        />
      </nav>
      {activeNav.opened && (
        <div
          className={cn(
            'share-modal fixed bottom-0 left-1/2 z-[9999] flex w-screen -translate-x-1/2 transform flex-col gap-3 bg-white px-6 py-4 text-primary drop-shadow-[0_0px_10px_rgba(0,0,0,0.2)] sm:px-0',
            shareButtons
              ? 'pointer-events-auto translate-y-0 opacity-100'
              : 'pointer-events-none translate-y-full opacity-0',
          )}
        >
          <div className='container'>
            <div className='flex items-center justify-between'>
              <p className='text-lg font-semibold'>Share to...</p>
              <IconButton
                icon={CloseIcon}
                variant='ghost'
                className='hover:bg-transparent'
                onClick={() => setShareButtons(false)}
                aria-label='Close navigation menu'
              />
            </div>
            {share && (
              <ul className='flex flex-col gap-6 pt-5'>
                {share?.facebook && (
                  <li className={Classes.shareButtonArticleItem}>
                    <UnstyledLink
                      href={`https://www.facebook.com/sharer/sharer.php?u=${pageURL}${pathname}`}
                      className={Classes.shareButtonArticleLink}
                      aria-label='Share on Facebook'
                      openNewTab
                    >
                      <FacebookIcon
                        fill='#010F1D'
                        className='pointer-events-none h-6 w-6 fill-primary sm:h-[26px] sm:w-[26px]'
                      />
                      <span className='pointer-events-none'>Facebook</span>
                    </UnstyledLink>
                  </li>
                )}
                {share?.linkedin && (
                  <li className={Classes.shareButtonArticleItem}>
                    <UnstyledLink
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${pageURL}${pathname}`}
                      className={Classes.shareButtonArticleLink}
                      aria-label='Share on Linkedin'
                      openNewTab
                    >
                      <LinkedinIcon
                        fill='#010F1D'
                        className='pointer-events-none h-6 w-6 fill-primary sm:h-[26px] sm:w-[26px]'
                      />
                      <span className='pointer-events-none'>Linkedin</span>
                    </UnstyledLink>
                  </li>
                )}
                {share?.twitter && (
                  <li className={Classes.shareButtonArticleItem}>
                    <UnstyledLink
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(siteConfig.description || '')}%0ARead%20more%3A%20${pageURL}${pathname}`}
                      className={Classes.shareButtonArticleLink}
                      aria-label='Share on Twitter'
                      openNewTab
                    >
                      <TwitterIcon
                        fill='#010F1D'
                        className='pointer-events-none h-6 w-6 fill-primary sm:h-[26px] sm:w-[26px]'
                      />
                      <span className='pointer-events-none'>Twitter</span>
                    </UnstyledLink>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
